import { useEffect } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Home, Library, User, LogOut, CalendarDays, LayoutGrid } from "lucide-react";
import { useMember } from "./MemberContext";
import { PageLoader, TierBadge } from "./ui";
import { InstallBanner } from "./InstallPrompt";

const NAV = [["/training/start", "Start", Home], ["/training/plan", "Plan", CalendarDays], ["/training/bibliothek", "Bibliothek", Library], ["/training/mehr", "Mehr", LayoutGrid], ["/training/profil", "Profil", User]];

export function TrainingHead() {
  useEffect(() => { if ("serviceWorker" in navigator) navigator.serviceWorker.register("/training-sw.js", { scope: "/training" }).catch(() => {}); }, []);
  return (
    <Helmet>
      <title>Personal Fußballtraining – Trainiere wie ein Profi</title>
      <meta name="description" content="Fußballtraining für zu Hause, im Park und auf dem Platz: Übungen, Wochenpläne und Fortschritt – für Kinder, Jugendliche und Erwachsene." />
      <link rel="manifest" href="/training-manifest.json" />
      <meta name="theme-color" content="#0A0A0A" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content="PFT Training" />
      <link rel="apple-touch-icon" href="/apple-touch-icon-training.png" />
      <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
    </Helmet>
  );
}

export function Brand({ to = "/training" }) {
  return (
    <Link to={to} className="font-['Bebas_Neue'] text-2xl tracking-tight text-white leading-none" data-testid="training-brand">
      PERSONAL <span className="text-[#c1ff72]">FUSSBALLTRAINING</span>
    </Link>
  );
}

export function MemberShell() {
  const { member, logout } = useMember();
  const loc = useLocation();
  if (member === null) return <PageLoader />;
  if (!member) return <Navigate to={`/training/login?next=${encodeURIComponent(loc.pathname)}`} replace />;
  return (
    <div className="min-h-[100dvh] bg-[#0A0A0A] text-white font-['Outfit']" data-testid="member-shell">
      <header className="sticky top-0 z-40 bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 h-16 flex items-center gap-8">
          <Brand to="/training/start" />
          <nav className="hidden md:flex gap-1 ml-6">
            {NAV.map(([to, lbl]) => (
              <NavLink key={to} to={to} data-testid={`nav-desktop-${lbl.toLowerCase()}`}
                className={({ isActive }) => `px-4 py-2 text-sm uppercase tracking-wider transition-colors ${isActive ? "text-[#c1ff72]" : "text-[#A1A1AA] hover:text-white"}`}>{lbl}</NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-3">
            <TierBadge tier={member.tier} testId="header-tier-badge" />
            <span className="hidden sm:block text-sm text-[#A1A1AA]" data-testid="header-member-name">{member.first_name}</span>
            <button onClick={logout} className="hidden md:flex items-center gap-1 text-sm text-[#71717A] hover:text-white" data-testid="logout-button"><LogOut className="w-4 h-4" />Abmelden</button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-5 lg:px-10 pt-8 pb-32 md:pb-16"><InstallBanner /><Outlet /></main>
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-[#0A0A0A]/90 backdrop-blur-xl border-t border-white/10 pb-[env(safe-area-inset-bottom)]" data-testid="bottom-nav">
        <div className="grid grid-cols-5">
          {NAV.map(([to, lbl, Icon]) => (
            <NavLink key={to} to={to} data-testid={`nav-mobile-${lbl.toLowerCase()}`}
              className={({ isActive }) => `flex flex-col items-center gap-1 py-3 text-[11px] uppercase tracking-wider ${isActive ? "text-[#c1ff72]" : "text-[#71717A]"}`}>
              <Icon className="w-5 h-5" />{lbl}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
